/**
 * Unwraps the new Documents API response format.
 *
 * The new API response format wraps all data in a standardized envelope:
 * {
 *   meta: { requestId, timestamp, apiVersion, traceId },
 *   response: { type, message, data, packages },
 *   error: { status, code, message, debugMessage, packages },
 *   isSuccess: boolean
 * }
 *
 * This utility extracts the actual data from the response envelope.
 *
 * @param {Object} response - The API response object
 * @returns {*} The unwrapped data, or the original response if not in the new format
 */
export const unwrapApiResponse = (response) => {
  // If response is null/undefined, return as-is
  if (!response) {
    return response;
  }

  // Check if this is the new wrapped format by looking for the isSuccess property
  // and either a response or error object
  const isNewFormat = (
    typeof response === 'object' &&
    'isSuccess' in response &&
    ('response' in response || 'error' in response)
  );

  if (!isNewFormat) {
    // Old format - return as-is
    return response;
  }

  // New format - check for success
  if (!response.isSuccess) {
    // Create an error with the API error details
    const errorInfo = response.error || {};
    const error = new Error(errorInfo.message || 'API request failed');
    error.status = errorInfo.status;
    error.code = errorInfo.code;
    error.debugMessage = errorInfo.debugMessage;
    error.packages = errorInfo.packages;
    error.meta = response.meta;
    throw error;
  }

  // Success - extract the data from response
  const responseData = response.response;

  if (!responseData) {
    return null;
  }

  // The actual data can be in response.data (for single items)
  // For list endpoints, check if there's a different structure
  if (responseData.data !== undefined) {
    // If data is a string that looks like JSON, try to parse it
    if (typeof responseData.data === 'string') {
      try {
        return JSON.parse(responseData.data);
      } catch {
        return responseData.data;
      }
    }
    return responseData.data;
  }

  // If no data field, return the response object itself
  // (some endpoints might structure data differently)
  return responseData;
};

/**
 * Checks if a response is in the new API format
 * @param {Object} response - The response to check
 * @returns {boolean} True if the response is in the new format
 */
export const isNewApiFormat = (response) => {
  return (
    typeof response === 'object' &&
    response !== null &&
    'isSuccess' in response &&
    ('response' in response || 'error' in response)
  );
};

/**
 * Helper to unwrap a fetch Response object
 * Use this when making direct fetch() calls instead of using the authenticated client
 *
 * @param {Response} response - The fetch Response object
 * @returns {Promise<*>} The unwrapped data
 * @throws {Error} If the response is not ok or API returned an error
 */
export const unwrapFetchResponse = async (response) => {
  const data = await response.json();

  // Check if this is the new wrapped format
  if (isNewApiFormat(data)) {
    return unwrapApiResponse(data);
  }

  // Old format - return as-is
  return data;
};

export default unwrapApiResponse;
